import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError, apiUrl } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { uploadFile } from '@/features/files/api'
import {
  CANDIDATE_SOURCES,
  CANDIDATE_SOURCE_LABELS,
  createCandidate,
  updateCandidate,
  type CandidateRead,
} from '@/features/recruitment/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const schema = z.object({
  full_name: z.string().trim().min(1, 'Enter a name').max(150),
  email: z.string().trim().min(1, 'Enter an email').email('Enter a valid email').max(255),
  phone: z.string().trim().max(30),
  source: z.union([z.enum(CANDIDATE_SOURCES), z.literal('')]),
  notes: z.string().trim().max(4000),
})

type FormValues = z.infer<typeof schema>

/** Create and edit in one dialog. `email` is fixed once a candidate exists -- see the API
 *  module note on `CandidateUpdate` -- so the field is disabled rather than hidden, which
 *  says why it cannot be touched instead of just removing it from view. */
export function CandidateFormDialog({
  open,
  candidate,
  onClose,
}: {
  open: boolean
  candidate?: CandidateRead
  onClose: () => void
}) {
  const isEdit = candidate !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const [resumeKey, setResumeKey] = useState(candidate?.resume_key ?? null)
  const [uploading, setUploading] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: candidate?.full_name ?? '',
      email: candidate?.email ?? '',
      phone: candidate?.phone ?? '',
      source: (candidate?.source as (typeof CANDIDATE_SOURCES)[number]) ?? '',
      notes: candidate?.notes ?? '',
    },
  })

  const guard = useDirtyGuard(open && (isDirty || resumeKey !== (candidate?.resume_key ?? null)))

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      setResumeKey(candidate?.resume_key ?? null)
      onClose()
    })
  }

  const pickResume = async (file: File) => {
    setUploading(true)
    try {
      const key = await uploadFile(file, 'candidate_resume')
      setResumeKey(key)
    } catch (error) {
      toast.error(
        error instanceof ApiError ? error.message : 'The resume could not be uploaded.',
      )
    } finally {
      setUploading(false)
    }
  }

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const shared = {
        full_name: values.full_name,
        phone: values.phone || null,
        resume_key: resumeKey,
        source: values.source || null,
        notes: values.notes || null,
      }
      return candidate
        ? updateCandidate(candidate.id, shared)
        : createCandidate({ ...shared, email: values.email })
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['candidates'] })
      void queryClient.invalidateQueries({ queryKey: ['candidate', saved.id] })
      toast.success(isEdit ? 'Candidate saved.' : 'Candidate added.')
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.status === 409
            ? 'A candidate with that email already exists.'
            : error.message
          : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${candidate.full_name}` : 'Add a candidate'}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="candidate-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Add candidate'}
            </Button>
          </>
        }
      >
        <form id="candidate-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Full name" error={errors.full_name?.message}>
              {(props) => <Input {...props} {...register('full_name')} autoComplete="off" />}
            </Field>
            <Field
              label="Email"
              error={errors.email?.message}
              hint={isEdit ? 'Fixed once a candidate exists.' : undefined}
            >
              {(props) => (
                <Input {...props} {...register('email')} type="email" autoComplete="off" disabled={isEdit} />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Phone" error={errors.phone?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('phone')} autoComplete="off" />}
            </Field>
            <Field label="Source" error={errors.source?.message} hint="Optional.">
              {(props) => (
                <select {...props} {...register('source')} className={SELECT_CLASS}>
                  <option value="">Unspecified</option>
                  {CANDIDATE_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {CANDIDATE_SOURCE_LABELS[source]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          <Field label="Résumé" hint="PDF or DOCX. Optional.">
            {() => (
              <div className="flex items-center gap-3">
                <input
                  ref={fileInput}
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="sr-only"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    event.target.value = ''
                    if (file) void pickResume(file)
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={uploading}
                  onClick={() => fileInput.current?.click()}
                >
                  {resumeKey ? 'Replace résumé' : 'Upload résumé'}
                </Button>
                {resumeKey && resumeKey === candidate?.resume_key && candidate?.resume_url ? (
                  <a
                    href={apiUrl(candidate.resume_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-brand-600 underline hover:text-brand-700 dark:text-brand-400"
                  >
                    {resumeKey.split('/').pop()}
                  </a>
                ) : (
                  resumeKey && (
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {resumeKey.split('/').pop()}
                    </span>
                  )
                )}
              </div>
            )}
          </Field>

          <Field label="Notes" error={errors.notes?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('notes')} autoComplete="off" />}
          </Field>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
